import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function BookingPopup({ isOpen, onClose, onNext }) {
    const [occasion, setOccasion] = useState("");
    const [service, setService] = useState("");
    const [numPeople, setNumPeople] = useState("");
    const [cooldown, setCooldown] = useState(false);

    /**
     * Auto-open popup only:
     * 1. If user has never completed it (seenPopup not set)
     * 2. If we are not in cooldown window
     */
    useEffect(() => {
        const seenPopup = localStorage.getItem("seenPopup");

        if (!seenPopup && !cooldown) {
            const timer = setTimeout(() => {
                onNext("open");
            }, 2000); // initial 2-second delay

            return () => clearTimeout(timer);
        }
    }, [onNext, cooldown]);

    /**
     * Starts a 60-second cooldown during which
     * popup will NOT auto-open again
     */
    const startCooldown = () => {
        setCooldown(true);
        setTimeout(() => {
            setCooldown(false);
        }, 60000); // ⏱️ 60 seconds
    };

    /**
     * Proceed button
     */
    const handleNext = () => {
        if (occasion && service && numPeople) {
            localStorage.setItem("seenPopup", "true");

            onNext({
                occasion,
                service,
                numPeople,
            });

            startCooldown();
        } else {
            alert("Please select occasion, service type, and number of people ✨");
        }
    };

    /**
     * Close popup manually
     */
    const handleClose = () => {
        onClose();
        startCooldown();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay */}
                    <motion.div
                        className="fixed inset-0 bg-black/50 backdrop-blur-md z-40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                    />

                    {/* Popup */}
                    <motion.div
                        className="fixed inset-0 flex items-center justify-center z-50"
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                    >
                        <div className="bg-white text-[#1F2937] border border-[#D4AF37] rounded-2xl shadow-xl w-[90%] max-w-lg p-8 relative">

                            {/* Close */}
                            <button
                                onClick={handleClose}
                                className="absolute top-3 right-4 text-[#D4AF37] hover:text-white text-2xl"
                            >
                                ✕
                            </button>

                            {/* Heading */}
                            <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#D4AF37] mb-2 text-center">
                                Let’s Begin Your Celebration 🍽️
                            </h2>

                            <p className="text-center text-[#4B5563] mb-6">
                                Tell us a bit about your event and we’ll curate the perfect menu.
                            </p>

                            {/* Fields */}
                            <div className="space-y-5">
                                {/* Occasion */}
                                <div>
                                    <label className="block text-sm mb-2 text-[#D4AF37]">
                                        Type of Occasion
                                    </label>
                                    <select
                                        value={occasion}
                                        onChange={(e) => setOccasion(e.target.value)}
                                        className="w-full p-3 rounded-md bg-[#F9FAFB] border border-[#D4AF37] text-[#1F2937] focus:ring-2 focus:ring-[#D4AF37]"
                                    >
                                        <option value="">Select occasion</option>
                                        <option>Wedding</option>
                                        <option>Birthday</option>
                                        <option>Corporate Event</option>
                                        <option>Engagement</option>
                                        <option>Traditional Ceremony</option>
                                        <option>Private Party</option>
                                        <option>Other</option>
                                    </select>
                                </div>

                                {/* Service */}
                                <div>
                                    <label className="block text-sm mb-2 text-[#D4AF37]">
                                        Type of Service
                                    </label>
                                    <select
                                        value={service}
                                        onChange={(e) => setService(e.target.value)}
                                        className="w-full p-3 rounded-md bg-[#F9FAFB] border border-[#D4AF37] text-[#1F2937] focus:ring-2 focus:ring-[#D4AF37]"
                                    >
                                        <option value="">Select service</option>
                                        <option>Buffet</option>
                                        <option>Plated Service</option>
                                        <option>Live Counter</option>
                                        <option>Packed Meal</option>
                                        <option>Outdoor Setup</option>
                                        <option>Custom</option>
                                    </select>
                                </div>

                                {/* Number of People */}
                                <div>
                                    <label className="block text-sm mb-2 text-[#D4AF37]">
                                        Number of People
                                    </label>
                                    <input
                                        type="number"
                                        min="50"
                                        max="50000"
                                        placeholder="50 – 50,000"
                                        value={numPeople}
                                        onChange={(e) => setNumPeople(e.target.value)}
                                        className="w-full p-3 rounded-md bg-[#F9FAFB] border border-[#D4AF37] text-[#1F2937] focus:ring-2 focus:ring-[#D4AF37]"
                                    />
                                </div>
                            </div>

                            {/* Next */}
                            <div className="text-center mt-8">
                                <button
                                    onClick={handleNext}
                                    className="bg-gradient-to-r from-[#D4AF37] to-[#B88A2D] text-white font-semibold px-8 py-3 rounded-full hover:scale-105 transition-transform"
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

